import ChallengeLanding from "./views/ChallengeLanding";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  SnackbarContent,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import * as React from "react";
import { GlobalHotKeys, configure } from "react-hotkeys";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { CARD_TRANSITION_ANIMATION_MS, NAV_CARDS } from "../Constants";
import {
  CardNameType,
  CardType,
  SettingsType,
  TransitionClassType,
  TutorialStepChangeType,
  TutorialStepType,
  TutorialUiIdType,
  UIType,
  isGatedStep,
} from "../Types";
import AudioContainer from "./base/AudioContainer";
import DesktopPanes from "./base/DesktopPanes";
import DisplayNameDialogContainer from "./base/DisplayNameDialogContainer";
import InstallAppButton from "./base/InstallAppButton";
import TutorialHud from "./base/TutorialHud";
import DataCenterGuide from "./base/DataCenterGuide";
import LowCashWarning from "./base/LowCashWarning";
import EventLogContainer from "./views/EventLogContainer";
import NavigationContainer from "./base/NavigationContainer";
import GameAppBarContainer from "./base/GameAppBar";
import VictoryDialogContainer from "./base/VictoryDialogContainer";
import SaveTransitionDialog from "./base/SaveTransitionDialog";
import BuildFacilities from "./views/BuildFacilities";
import CustomGameContainer from "./views/CustomGameContainer";
import DataCenterSetupContainer from "./views/DataCenterSetupContainer";
import FacilitiesContainer from "./views/FacilitiesContainer";
import InsightsContainer from "./views/InsightsContainer";
import LoadingContainer from "./views/LoadingContainer";
import MainMenuContainer from "./views/MainMenuContainer";
import ManualHelpPopover from "./base/ManualHelpPopover";
import ManualContainer from "./views/ManualContainer";
import NewGameContainer from "./views/NewGameContainer";
import NewGameDetailsContainer from "./views/NewGameDetailsContainer";
import SettingsContainer from "./views/SettingsContainer";
import SavedGames from "./views/SavedGames";
import { navigate, navigateBack } from "../reducers/Card";
import {
  reprioritizeFacility,
  setSpeed,
  togglePauseFacility,
} from "../reducers/Game";
import { selectTutorialHiddenUi } from "../reducers/Tutorial";
import { snackbarOpen, manualHelpClose, manualHelpOpen } from "../reducers/UI";
import { isDesktopScreen, isPaneLayout } from "../Globals";
import { store } from "../Store";

const AchievementGallery = React.lazy(() =>
  import(
    /* webpackChunkName: "achievements-ui" */ "../achievements/Interface"
  ).then(({ AchievementGallery }) => ({ default: AchievementGallery })),
);

// The in-game cards share one stable transition key above the desktop breakpoint. Facilities
// and Insights stay mounted while Events replaces Insights, or joins it on an ultrawide screen.
const DESKTOP_PANES_KEY = "DESKTOP_PANES";

// And the same again for the two-column layout below it, where Facilities is pinned and the nav
// swaps what is beside it -- the pinned column would otherwise slide off with the card transition
const TABLET_PANES_KEY = "TABLET_PANES";

// How many facilities the number row can reach. Nine because that is how many number keys
// there are; a longer fleet is still reachable by mouse, and by the row actions
const FACILITY_SLOTS = [1, 2, 3, 4, 5, 6, 7, 8, 9];

// Shifted, because the bare number row is the speed control and has been for far longer than
// the fleet has had shortcuts of its own
const facilitySlotKey = (slot: number) => `shift+${slot}`;

// react-hotkeys' default ignoreEventsCondition treats every <input> as a text field and drops
// the keydown entirely -- but the capacity sliders on the build screens and the rate slider in
// Finances are MUI Sliders, which render as a bare <input type="range">. Just
// clicking one leaves it focused, and from then on every shortcut silently did nothing until
// the player happened to click something else -- the "some element is pulling focus" bug.
// These types don't take character input, so there's nothing for a shortcut key to clobber.
// Escape is exempted outright too, so it can always back out of a screen even if focus never
// left an actual text field.
const NON_TEXT_INPUT_TYPES = new Set([
  "range",
  "checkbox",
  "radio",
  "button",
  "color",
  "submit",
  "reset",
  "image",
  "file",
]);
configure({
  ignoreEventsCondition: (event: KeyboardEvent) => {
    if (document.querySelector('[data-data-center-guide="true"]')) return true;
    if (document.querySelector('[data-scenario-choice="true"]')) return true;
    if (document.querySelector('[data-manual-help="true"]')) return true;
    if (document.querySelector('[data-customer-programs="true"]')) return true;
    if (event.key === "Escape") {
      return false;
    }
    const target = event.target as HTMLElement | null;
    const tagName = target?.tagName?.toLowerCase();
    if (!tagName) {
      return false;
    }
    if (
      tagName === "input" &&
      NON_TEXT_INPUT_TYPES.has((target as HTMLInputElement).type)
    ) {
      return false;
    }
    return (
      tagName === "input" ||
      tagName === "select" ||
      tagName === "textarea" ||
      !!target?.isContentEditable
    );
  },
});

// Keep in sync with SHORTCUTS in base/KeyboardShortcuts, which is what the Manual and Settings
// both list -- exported so a test can hold the two against each other rather than a comment
// asking politely. react-hotkeys ignores key events from inputs, so `?` still types into a
// text field
export const keyMap = {
  PAUSED: ["`", "space", "0"],
  SLOW: "1",
  NORMAL: "2",
  FAST: "3",
  ULTRA: "4",
  FACILITIES: "q",
  INSIGHTS: ["w", "e"],
  EVENTS: "r",
  BUILD_GENERATOR: "g",
  BUILD_STORAGE: "s",
  PRIORITIZE_EARLIER: "[",
  PRIORITIZE_LATER: "]",
  MANUAL: ["?", "shift+/"],
  ESCAPE: "esc",
  ...Object.fromEntries(
    FACILITY_SLOTS.map((slot: number) => [
      `TOGGLE_FACILITY_${slot}`,
      facilitySlotKey(slot),
    ]),
  ),
};

/**
 * Whether the keys that act on the game should do anything right now.
 *
 * The hotkeys are global, so they fire over the main menu and the manual too -- and a replay is
 * somebody else's run, which the viewer does not get to reorder.
 */
function canPlay(): boolean {
  const state = store.getState();
  return (
    state.game.inGame &&
    !state.game.replayPlayback &&
    isNavCard(state.card.name)
  );
}

// Pausing from the keyboard offers the same undo the row's pause button does: the fleet is a
// list of similar-looking rows, and a mistyped number is the likeliest way to reach one
function togglePauseSlot(slot: number) {
  if (!canPlay()) {
    return;
  }
  const facility = store.getState().game.facilities[slot - 1];
  if (!facility || facility.yearsToBuildLeft > 0) {
    return;
  }
  const wasPaused = facility.paused;
  store.dispatch(togglePauseFacility(facility.id));
  store.dispatch(
    snackbarOpen({
      message: `${wasPaused ? "Resumed" : "Paused"} ${facility.name}`,
      actionLabel: "Undo",
      action: () => store.dispatch(togglePauseFacility(facility.id)),
      open: true,
      timeout: 6000,
    }),
  );
}

// Dispatch order is a core mechanic, and until now the only ways to change it were a drag and a
// pair of buttons that appear on hover. These move the open row, which is the one the other two
// panes are already reporting on
function reprioritizeSelected(delta: number) {
  if (!canPlay()) {
    return;
  }
  const { game, ui } = store.getState();
  const spotInList = game.facilities.findIndex(
    (f) => f.id === ui.selectedFacilityId,
  );
  const destination = spotInList + delta;
  if (
    spotInList < 0 ||
    destination < 0 ||
    destination >= game.facilities.length
  ) {
    return;
  }
  store.dispatch(reprioritizeFacility({ spotInList, delta }));
}

// A shortcut must not reach a control the tutorial is still keeping hidden: a new player who
// presses space on Mission 1's first step would otherwise start a clock they haven't met yet
function uiHidden(id: TutorialUiIdType): boolean {
  return selectTutorialHiddenUi(store.getState()).includes(id);
}

const shortcutHandlers = {
  // Space is one of this handler's own keys, and the browser's default action for it is to
  // scroll the page -- which fires alongside the pause because react-hotkeys doesn't call
  // preventDefault for you. Only this binding needs it: the other two (`` ` `` and `0`) have no
  // native behaviour of their own
  PAUSED: (e?: KeyboardEvent) => {
    e?.preventDefault();
    if (!uiHidden("speed")) {
      store.dispatch(setSpeed("PAUSED"));
    }
  },
  SLOW: () => {
    if (!uiHidden("speed")) {
      store.dispatch(setSpeed("SLOW"));
    }
  },
  NORMAL: () => {
    if (!uiHidden("speed")) {
      store.dispatch(setSpeed("NORMAL"));
    }
  },
  FAST: () => {
    if (!uiHidden("speed")) {
      store.dispatch(setSpeed("FAST"));
    }
  },
  // Desktop only, like its button
  ULTRA: () => {
    if (!uiHidden("speed") && isDesktopScreen()) {
      store.dispatch(setSpeed("ULTRA"));
    }
  },
  FACILITIES: () => {
    if (!uiHidden("nav")) {
      store.dispatch(navigate("FACILITIES"));
    }
  },
  INSIGHTS: () => {
    if (!uiHidden("nav")) {
      store.dispatch(navigate("INSIGHTS"));
    }
  },
  EVENTS: () => {
    if (!uiHidden("nav")) {
      store.dispatch(navigate("EVENTS"));
    }
  },
  BUILD_GENERATOR: () => {
    if (canPlay() && !uiHidden("build")) {
      store.dispatch(
        navigate({ name: "BUILD_GENERATORS", dontRemember: true }),
      );
    }
  },
  BUILD_STORAGE: () => {
    if (canPlay() && !uiHidden("build")) {
      store.dispatch(navigate({ name: "BUILD_STORAGE", dontRemember: true }));
    }
  },
  PRIORITIZE_EARLIER: () =>
    !uiHidden("facilityActions") && reprioritizeSelected(-1),
  PRIORITIZE_LATER: () =>
    !uiHidden("facilityActions") && reprioritizeSelected(1),
  MANUAL: () => {
    store.dispatch(navigate("MANUAL"));
  },
  // Match each screen's close/back control. The build catalog closes to Facilities with a
  // fade, even when opened from another pane or after switching categories; other screens
  // follow their Back button through history.
  ESCAPE: () => {
    const { card, game } = store.getState();
    if (game.inGame && !isNavCard(card.name)) {
      store.dispatch(
        card.name.startsWith("BUILD_")
          ? navigate("FACILITIES")
          : navigateBack(),
      );
      // Escape may arrive before the gallery's entry focus settles.
      if (card.name === "ACHIEVEMENTS")
        window.setTimeout(
          () =>
            document
              .querySelector<HTMLElement>("[data-achievements-trigger]")
              ?.focus(),
          350,
        );
    }
  },
  ...Object.fromEntries(
    FACILITY_SLOTS.map((slot: number) => [
      `TOGGLE_FACILITY_${slot}`,
      () => !uiHidden("facilityActions") && togglePauseSlot(slot),
    ]),
  ),
};

export interface StateProps {
  card: CardType;
  settings: SettingsType;
  ui: UIType;
  transition: TransitionClassType;
  scenarioId: number;
  tutorialStep: number;
  tutorialSteps?: TutorialStepType[];
}

// Moved to Types so the tutorial gate middleware can share it without a component import;
// re-exported here for existing consumers
export type { TutorialStepChangeType } from "../Types";

export interface DispatchProps {
  closeDialog: () => void;
  closeSnackbar: () => void;
  onTutorialStep: (change: TutorialStepChangeType) => void;
  onTutorialEnd: (tutorialSteps: TutorialStepType[] | undefined) => void;
}

export interface Props extends StateProps, DispatchProps {}

export function isNavCard(name: CardNameType) {
  return NAV_CARDS.indexOf(name) !== -1;
}

const SNACKBAR_SWIPE_DISTANCE = 56;
const SNACKBAR_SWIPE_EXIT_MS = 180;

/** A toast follows the dominant axis of a drag: sideways either way, or down only. */
export function snackbarSwipeOffset(
  start: { x: number; y: number },
  end: { x: number; y: number },
): { axis: "x" | "y"; distance: number } {
  const x = end.x - start.x;
  const y = Math.max(0, end.y - start.y);
  return Math.abs(x) > y
    ? { axis: "x", distance: x }
    : { axis: "y", distance: y };
}

export function shouldDismissSnackbarSwipe(
  start: { x: number; y: number },
  end: { x: number; y: number },
): boolean {
  return (
    Math.abs(end.x - start.x) >= SNACKBAR_SWIPE_DISTANCE ||
    end.y - start.y >= SNACKBAR_SWIPE_DISTANCE
  );
}

export default class Compositor extends React.Component<Props, {}> {
  private resizeTimeout: ReturnType<typeof setTimeout> | undefined;
  private snackbarContentRef = React.createRef<HTMLDivElement>();
  private dialogCancelRef = React.createRef<HTMLButtonElement>();
  private snackbarDrag?: { x: number; y: number; pointerId: number };
  private snackbarDismissTimeout: ReturnType<typeof setTimeout> | undefined;

  // react-transition-group falls back to ReactDOM.findDOMNode when no nodeRef is given, and
  // React 19 removed findDOMNode outright. TransitionGroup holds the exiting and the entering
  // child at the same time, so each needs its own ref rather than one shared one -- they are
  // cached per transition key, of which there is only ever the finite set of card names.
  private nodeRefs = new Map<string, React.RefObject<HTMLDivElement>>();

  private nodeRefFor(key: string): React.RefObject<HTMLDivElement> {
    let ref = this.nodeRefs.get(key);
    if (!ref) {
      ref = React.createRef<HTMLDivElement>();
      this.nodeRefs.set(key, ref);
    }
    return ref;
  }

  // isDesktopScreen() is read straight from the DOM rather than from state, so a resize needs
  // its own trigger -- forceUpdate skips shouldComponentUpdate, which otherwise blocks re-renders
  // when nothing about the current card has changed
  public handleResize = () => {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => this.forceUpdate(), 100);
  };

  public componentDidMount() {
    window.addEventListener("resize", this.handleResize);
  }

  public componentDidUpdate(prevProps: Props) {
    if (
      this.props.ui.dialog.open &&
      (this.props.ui.dialog.destructive || this.props.ui.dialog.focusCancel) &&
      this.props.ui.dialog !== prevProps.ui.dialog
    ) {
      this.dialogCancelRef.current?.focus();
    }
    // A new toast replaces one that is still flying out: keep it on screen and open
    const { snackbar } = this.props.ui;
    if (
      snackbar.open &&
      (!prevProps.ui.snackbar.open ||
        snackbar.message !== prevProps.ui.snackbar.message)
    ) {
      clearTimeout(this.snackbarDismissTimeout);
      this.snackbarDismissTimeout = undefined;
      this.resetSnackbarDrag();
    }
  }

  public componentWillUnmount() {
    window.removeEventListener("resize", this.handleResize);
    clearTimeout(this.resizeTimeout);
    clearTimeout(this.snackbarDismissTimeout);
  }

  public moveTutorial = (toStep: number) => {
    this.props.onTutorialStep({
      fromStep: this.props.tutorialStep,
      toStep,
      tutorialSteps: this.props.tutorialSteps,
      scenarioId: this.props.scenarioId,
      currentCard: this.props.card.name,
    });
  };

  public snackbarActionClicked(e: React.MouseEvent<HTMLElement>) {
    if (this.props.ui.snackbar.action) {
      this.props.ui.snackbar.action(e);
    }
  }

  private resetSnackbarDrag = () => {
    this.snackbarDrag = undefined;
    const content = this.snackbarContentRef.current;
    if (content) {
      content.style.transition = "";
      content.style.transform = "";
      content.style.opacity = "";
    }
  };

  private snackbarPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    // A press on Missions or the close button has to stay a click. Capturing the pointer
    // retargets its pointerup to the snackbar itself, so the browser fires the click there
    // instead of on the button and neither one ever did anything
    if (
      event.button !== 0 ||
      this.snackbarDismissTimeout !== undefined ||
      (event.target as HTMLElement).closest("button")
    ) {
      return;
    }
    this.snackbarDrag = {
      x: event.clientX,
      y: event.clientY,
      pointerId: event.pointerId,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  private snackbarPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const start = this.snackbarDrag;
    if (!start || start.pointerId !== event.pointerId) {
      return;
    }
    const { axis, distance } = snackbarSwipeOffset(start, {
      x: event.clientX,
      y: event.clientY,
    });
    const style = event.currentTarget.style;
    // Follow the finger directly; the release animates the snap back or the fly-out
    style.transition = "none";
    style.transform =
      axis === "x" ? `translateX(${distance}px)` : `translateY(${distance}px)`;
    style.opacity = String(
      1 - Math.min(0.6, Math.abs(distance) / (SNACKBAR_SWIPE_DISTANCE * 4)),
    );
  };

  private snackbarPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    const start = this.snackbarDrag;
    const content = this.snackbarContentRef.current;
    const end = { x: event.clientX, y: event.clientY };
    if (
      start &&
      content &&
      start.pointerId === event.pointerId &&
      shouldDismissSnackbarSwipe(start, end)
    ) {
      this.snackbarDrag = undefined;
      // Carry on in the swipe's direction and fade, then close. The toast's own exit
      // transition (a shrink toward its anchor) only starts once it is already invisible,
      // so the player sees one continuous slide rather than a slide that turns into a shrink
      const { axis, distance } = snackbarSwipeOffset(start, end);
      const exit =
        axis === "x"
          ? `translateX(${Math.sign(distance) * window.innerWidth}px)`
          : `translateY(${content.offsetHeight + 96}px)`;
      content.style.transition = `transform ${SNACKBAR_SWIPE_EXIT_MS}ms ease-out, opacity ${SNACKBAR_SWIPE_EXIT_MS}ms ease-out`;
      content.style.transform = exit;
      content.style.opacity = "0";
      this.snackbarDismissTimeout = setTimeout(() => {
        this.snackbarDismissTimeout = undefined;
        this.props.closeSnackbar();
      }, SNACKBAR_SWIPE_EXIT_MS);
      return;
    }
    this.resetSnackbarDrag();
  };

  private renderCard(): React.JSX.Element {
    const isPanes = isNavCard(this.props.card.name) && isPaneLayout();
    const { tutorialStep, tutorialSteps } = this.props;
    // A step that keeps the side panes back gets the fleet on its own. Dropped here rather than
    // hidden in CSS: the grid's tracks come from the pane count, so a hidden pane would leave an
    // empty column and a splitter to nowhere
    if (
      isPanes &&
      tutorialSteps?.[tutorialStep]?.hideUi?.includes("sidePanes")
    ) {
      return (
        <div
          className={`${isDesktopScreen() ? "desktop-layout" : "pane-layout"} flexContainer`}
        >
          <GameAppBarContainer />
          <DesktopPanes>
            <FacilitiesContainer />
          </DesktopPanes>
        </div>
      );
    }
    // Give analysis room to breathe: the fleet stays beside one configurable Insights workbench
    // instead of splitting the width between separate Finance and Forecast chart columns.
    if (isPanes && isDesktopScreen()) {
      return (
        <div className="desktop-layout flexContainer">
          <GameAppBarContainer />
          <DesktopPanes>
            <FacilitiesContainer />
            <InsightsContainer />
            <EventLogContainer />
          </DesktopPanes>
        </div>
      );
    }
    // Laptops and landscape tablets keep the fleet pinned beside Insights or Events.
    if (isPanes) {
      return (
        <div className="pane-layout flexContainer">
          <GameAppBarContainer />
          <DesktopPanes>
            <FacilitiesContainer />
            <div className="secondary-pane">
              {this.props.card.name === "EVENTS" ? (
                <EventLogContainer />
              ) : (
                <InsightsContainer />
              )}
              <NavigationContainer />
            </div>
          </DesktopPanes>
        </div>
      );
    }
    switch (this.props.card.name) {
      case "BUILD_GENERATORS":
      case "BUILD_STORAGE":
      case "BUILD_INTERTIES":
        return <BuildFacilities />;
      case "INSIGHTS":
        return <InsightsContainer />;
      case "EVENTS":
        return <EventLogContainer />;
      case "FACILITIES":
        return <FacilitiesContainer />;
      case "SETTINGS":
        return <SettingsContainer />;
      case "ACHIEVEMENTS":
        return (
          <React.Suspense fallback={<p role="status">Loading achievements…</p>}>
            <AchievementGallery />
          </React.Suspense>
        );
      case "SAVED_GAMES":
        return <SavedGames />;
      case "MAIN_MENU":
        return <MainMenuContainer />;
      case "MANUAL":
        return <ManualContainer />;
      case "LOADING":
        return <LoadingContainer />;
      case "NEW_GAME":
        return <NewGameContainer />;
      case "NEW_GAME_DETAILS":
        return <NewGameDetailsContainer />;
      case "CHALLENGE":
        return <ChallengeLanding />;
      case "CUSTOM_GAME":
        return <CustomGameContainer />;
      case "DATA_CENTER_SETUP":
        return <DataCenterSetupContainer />;
      default:
        throw new Error("Unknown card " + this.props.card.name);
    }
  }

  public shouldComponentUpdate(nextProps: Props) {
    if (this.props.tutorialStep !== nextProps.tutorialStep) {
      return true;
    }

    if (
      this.props.ui.dialog !== nextProps.ui.dialog ||
      this.props.ui.snackbar !== nextProps.ui.snackbar ||
      this.props.ui.manualHelpEntry !== nextProps.ui.manualHelpEntry
    ) {
      return true;
    }

    // A new loading request must remount even when it replaces another loading request.
    if (this.props.card.name === nextProps.card.name) {
      return (
        nextProps.card.name === "LOADING" &&
        this.props.card.ts !== nextProps.card.ts
      );
    }

    return true;
  }

  public render() {
    const { tutorialStep, ui, closeDialog, tutorialSteps, closeSnackbar } =
      this.props;
    const currentTutorialStep =
      tutorialSteps && tutorialStep >= 0 && tutorialStep < tutorialSteps.length
        ? tutorialSteps[tutorialStep]
        : undefined;
    const canGoBack = !!(
      currentTutorialStep &&
      tutorialStep > 0 &&
      !isGatedStep(currentTutorialStep) &&
      !isGatedStep(tutorialSteps![tutorialStep - 1])
    );

    // Keep shared panes mounted when their navigation changes, so the pinned fleet stays visible.
    // A quick retry may start before the previous loading view finishes exiting.
    // A fresh key guarantees the new mission runs its loading lifecycle.
    const transitionKey =
      this.props.card.name === "LOADING"
        ? `LOADING:${this.props.card.ts}`
        : this.props.card.name.startsWith("BUILD_")
          ? "BUILD_FACILITIES"
          : !isNavCard(this.props.card.name)
            ? this.props.card.name
            : isDesktopScreen()
              ? DESKTOP_PANES_KEY
              : isPaneLayout()
                ? TABLET_PANES_KEY
                : this.props.card.name;
    const transitionNodeRef = this.nodeRefFor(transitionKey);
    // Chrome the step hides is dropped by app.scss rules on this attribute, so no pane or bar
    // needs to know about tutorials to step aside
    const hiddenUi = currentTutorialStep?.hideUi;
    const menuLayout =
      !isNavCard(this.props.card.name) &&
      !this.props.card.name.startsWith("BUILD_");
    const singlePanel =
      !isNavCard(this.props.card.name) ||
      !isPaneLayout() ||
      hiddenUi?.includes("sidePanes");

    // See https://medium.com/lalilo/dynamic-transitions-with-react-router-and-react-transition-group-69ab795815c9
    // for more details on use of childFactory in TransitionGroup
    return (
      <div
        className={`app_container${singlePanel ? " single-panel" : ""}${menuLayout ? " menu-layout" : ""}`}
        data-tutorial-hide={hiddenUi?.length ? hiddenUi.join(" ") : undefined}
      >
        <GlobalHotKeys keyMap={keyMap} handlers={shortcutHandlers} />
        <TransitionGroup
          className="cardTransitions"
          childFactory={(child) =>
            // @types/react 19 defaults ReactElement's props to unknown rather than any,
            // so the element has to be named before cloneElement will accept classNames
            React.cloneElement(
              child as React.ReactElement<{ classNames: TransitionClassType }>,
              { classNames: this.props.transition },
            )
          }
        >
          <CSSTransition
            key={transitionKey}
            nodeRef={transitionNodeRef}
            onEnter={() => {
              if (transitionNodeRef.current)
                transitionNodeRef.current.inert = false;
            }}
            onExit={() => {
              // The outgoing screen stays visible while fading, but can't receive input.
              if (transitionNodeRef.current)
                transitionNodeRef.current.inert = true;
            }}
            onExited={() => {
              if (transitionKey.startsWith("LOADING:")) {
                this.nodeRefs.delete(transitionKey);
              }
            }}
            classNames={""}
            timeout={{
              enter: CARD_TRANSITION_ANIMATION_MS,
              exit: CARD_TRANSITION_ANIMATION_MS,
            }}
          >
            <main
              className={`base_main${
                // Fade in the setup linked from the separate guide document on its first load
                // (ts 0 is the startup card).
                this.props.card.ts === 0 &&
                this.props.card.name === "DATA_CENTER_SETUP"
                  ? " pageEnter"
                  : ""
              }`}
              ref={transitionNodeRef}
            >
              {this.renderCard()}
            </main>
          </CSSTransition>
        </TransitionGroup>
        {tutorialSteps &&
          currentTutorialStep &&
          this.props.card.name !== "LOADING" &&
          this.props.card.name !== "MANUAL" &&
          !ui.manualHelpEntry && (
            <TutorialHud
              desktop={isDesktopScreen()}
              step={currentTutorialStep}
              stepIndex={tutorialStep}
              totalSteps={tutorialSteps.length}
              canGoBack={canGoBack}
              onBack={() => this.moveTutorial(tutorialStep - 1)}
              onNext={() => this.moveTutorial(tutorialStep + 1)}
              onExit={() => this.props.onTutorialEnd(tutorialSteps)}
            />
          )}
        <DataCenterGuide />
        <LowCashWarning />
        <ManualHelpPopover
          entry={ui.manualHelpEntry}
          onClose={() => store.dispatch(manualHelpClose())}
          onRelated={(title: string) => store.dispatch(manualHelpOpen(title))}
        />
        <Dialog
          open={ui.dialog.open}
          aria-labelledby="app-dialog-title"
          slotProps={{
            transition: {
              onEntered: () => {
                if (
                  this.props.ui.dialog.destructive ||
                  this.props.ui.dialog.focusCancel
                )
                  this.dialogCancelRef.current?.focus();
              },
            },
          }}
          // v9 replaced `disableEscapeKeyDown` with filtering on the close reason. A
          // notCancellable dialog is one whose buttons are the only way forward - the end of a
          // run, the end of a tutorial - so a backdrop click has to be refused alongside Esc,
          // or dismissing it strands the player in a game that's already over
          onClose={() => {
            if (ui.dialog.notCancellable) {
              return;
            }
            closeDialog();
          }}
        >
          <DialogTitle id="app-dialog-title">{ui.dialog.title}</DialogTitle>
          <DialogContent>{ui.dialog.message}</DialogContent>
          <DialogActions>
            {ui.dialog.offerInstall && (
              <InstallAppButton label="Install for later" afterMilestone />
            )}
            {ui.dialog.secondaryAction && (
              <Button color="primary" onClick={ui.dialog.secondaryAction}>
                {ui.dialog.secondaryLabel || "Close"}
              </Button>
            )}
            {ui.dialog.tertiaryAction && (
              <Button color="inherit" onClick={ui.dialog.tertiaryAction}>
                {ui.dialog.tertiaryLabel}
              </Button>
            )}
            {!ui.dialog.notCancellable && (
              <Button
                color="primary"
                autoFocus={ui.dialog.destructive || ui.dialog.focusCancel}
                ref={this.dialogCancelRef}
                onClick={closeDialog}
              >
                {ui.dialog.closeText || (ui.dialog.action ? "Cancel" : "OK")}
              </Button>
            )}
            {ui.dialog.action && (
              <Button
                color={ui.dialog.destructive ? "error" : "primary"}
                variant="contained"
                onClick={ui.dialog.action}
              >
                {ui.dialog.actionLabel || "OK"}
              </Button>
            )}
          </DialogActions>
        </Dialog>
        <Snackbar
          className={`snackbar ${isNavCard(this.props.card.name) ? "snackbar-above-nav" : ""}`}
          // Bottom left is the MUI default, which on a wide screen leaves the toast hanging
          // off the side of the centered app frame
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          open={ui.snackbar.open}
          autoHideDuration={ui.snackbar.timeout}
          onClose={closeSnackbar}
        >
          <SnackbarContent
            ref={this.snackbarContentRef}
            className="snackbarContent"
            message={<span>{ui.snackbar.message}</span>}
            onPointerDown={this.snackbarPointerDown}
            onPointerMove={this.snackbarPointerMove}
            onPointerUp={this.snackbarPointerUp}
            onPointerCancel={this.resetSnackbarDrag}
            action={
              <>
                {ui.snackbar.actionLabel && (
                  <Button
                    onClick={(e: React.MouseEvent<HTMLElement>) =>
                      this.snackbarActionClicked(e)
                    }
                  >
                    {ui.snackbar.actionLabel}
                  </Button>
                )}
                <IconButton
                  className="snackbarClose"
                  aria-label="Dismiss notification"
                  size="small"
                  onClick={closeSnackbar}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </>
            }
          />
        </Snackbar>
        {/* Connected, so they still update when shouldComponentUpdate blocks this component --
            neither is driven by the current card, and both can open over any of them */}
        <VictoryDialogContainer />
        <SaveTransitionDialog />
        <DisplayNameDialogContainer />
        <AudioContainer />
      </div>
    );
  }
}
